import React, { useRef, useState } from 'react';
import { ThreadMessage } from '../callCenterAllTicketsTypes';
import styles from './ConversationThread.module.css';

interface ConversationThreadProps {
  messages: ThreadMessage[];
  /**
   * Sends the reply text via parent (which calls the backend through the
   * tickets store). Resolve when the message is persisted so the textarea
   * can be cleared.
   */
  onSendReply?: (body: string) => Promise<void> | void;
  busy?: boolean;
}

export const ConversationThread: React.FC<ConversationThreadProps> = ({
  messages,
  onSendReply,
  busy,
}) => {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = async () => {
    const body = reply.trim();
    if (!body || !onSendReply) return;
    setSending(true);
    try {
      await onSendReply(body);
      setReply('');
    } finally {
      setSending(false);
    }
  };

  // Tickets endpoint has no upload field today, so attachments are surfaced
  // inline in the reply body. Click on 📎 → file picker → "[Attachment: name]"
  // appended to the textarea.
  const handleAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReply((prev) =>
      prev.trim() ? `${prev}\n[Attachment: ${file.name}]` : `[Attachment: ${file.name}]`
    );
    e.target.value = '';
  };

  const isBusy = busy || sending;

  return (
    <div className={styles.wrap}>
      <div className={styles.thread}>
        {messages.map((msg) => (
          <div key={msg.id} className={styles.message}>
            <div className={styles.headerRow}>
              <span className={`${styles.avatar} ${styles[msg.avatarColor]}`}>
                {msg.senderInitials}
              </span>
              <span className={styles.sender}>
                {msg.senderName} <span className={styles.role}>({msg.senderRole})</span>
              </span>
              <span className={styles.timestamp}>· {msg.timestamp}</span>
            </div>
            <p className={styles.body}>{msg.body}</p>
          </div>
        ))}
      </div>

      <textarea
        className={styles.replyBox}
        rows={3}
        placeholder="Type your reply or internal note..."
        value={reply}
        onChange={(e) => setReply(e.target.value)}
        disabled={isBusy}
      />
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleAttach}
        style={{ display: 'none' }}
      />
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.attachBtn}
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
        >
          📎 Attachment
        </button>
        <button
          type="button"
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={isBusy || !reply.trim() || !onSendReply}
        >
          {isBusy ? 'Sending…' : 'Send Reply'}
        </button>
      </div>
    </div>
  );
};
